import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Image, Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/AppText';
import { useRouter } from 'expo-router';
import {
  BarChart3,
  Briefcase,
  Grid2x2,
  Image as ImageIcon,
  Lock,
  Pencil,
  Plus,
  Users,
} from 'lucide-react-native';
import { colors, spacing } from '@tabbio/tokens';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { CompanyJobSummary, PublicProfileAnalyticsSummary } from '@tabbio/types';
import { useTheme } from '../../hooks/useTheme';
import { AppSwitch } from '../common/AppSwitch';
import { useHaptics } from '../../hooks/useHaptics';
import { useToast } from '../../hooks/useToast';
// The provider-free upgrade hook: it routes (native) / opens the dialog (web)
// without needing UpgradeModalProvider above it, so the paywall still works if
// this sheet is ever reached before the protected provider tree mounts.
import { useUpgradeFromFormSheet } from '../../hooks/useUpgradeFromFormSheet';
import {
  availableJobLifecycleActions,
  useJobLifecycleActions,
  type JobLifecycleAction,
} from '../../hooks/useJobLifecycleActions';
import {
  formatCompanyJobLifecycle,
  getCompanyJobLifecycle,
  getCompanyJobMetaLine,
} from '../../lib/company-jobs';
import type {
  PublicProfileOpenRolesLayout,
} from '../../lib/public-profile-builder';
import { uploadImageAsset } from '../../lib/image-upload';
import { pickImageForUpload } from '../../lib/pick-image';
import { fetchPublicProfileAnalytics } from '../../lib/public-profiles-api';
import { formatAnalyticsDate } from '../analytics/analytics-ui';
import { BRAND } from '../analytics/cv-analytics-ui';
import {
  ProfileGroupCard,
  ProfileSectionHeader,
  ProfileSheetScaffold,
} from './ProfileSheetScaffold';
import { usePublicProfileBuilderModel } from './PublicProfileBuilderProvider';
import {
  getOpenRolesLayout,
  getOpenRolesThumbnail,
  isTalentPoolEnabled,
  useCompanyOpenRoles,
  withOpenRolesLayout,
  withOpenRolesThumbnail,
  withTalentPoolEnabled,
} from './useCompanyOpenRoles';
import {
  getOpenRolesBlockClicks,
  getTopSourceShares,
  getViewToApplyRate,
} from '../../lib/public-profile-open-roles';

type OpenRolesTabId = 'jobs' | 'layout' | 'thumbnail' | 'insights';
type InsightsRangeId = '7d' | '30d' | 'all';

const TAB_ITEMS: { id: OpenRolesTabId; icon: React.ComponentType<any>; labelKey: string }[] = [
  { id: 'jobs', icon: Briefcase, labelKey: 'publicProfile.openRoles.tabs.jobs' },
  { id: 'layout', icon: Grid2x2, labelKey: 'publicProfile.openRoles.tabs.layout' },
  { id: 'thumbnail', icon: ImageIcon, labelKey: 'publicProfile.openRoles.tabs.thumbnail' },
  { id: 'insights', icon: BarChart3, labelKey: 'publicProfile.openRoles.tabs.insights' },
];

const LAYOUT_OPTIONS: { id: PublicProfileOpenRolesLayout; titleKey: string; descKey: string }[] = [
  {
    id: 'tabs',
    titleKey: 'publicProfile.openRoles.layout.byDepartment',
    descKey: 'publicProfile.openRoles.layout.byDepartmentDesc',
  },
  {
    id: 'cards',
    titleKey: 'publicProfile.openRoles.layout.cards',
    descKey: 'publicProfile.openRoles.layout.cardsDesc',
  },
  {
    id: 'compact',
    titleKey: 'publicProfile.openRoles.layout.compact',
    descKey: 'publicProfile.openRoles.layout.compactDesc',
  },
];

const ZERO_INSIGHTS_TOTALS = {
  views: 0,
  profileViews: 0,
  cvViews: 0,
  shareLinkViews: 0,
  uniqueVisitors: 0,
  shares: 0,
  ctaClicks: 0,
  contactClicks: 0,
  downloads: 0,
  blockClicks: 0,
  jobApplyClicks: 0,
};

const INSIGHTS_SOURCE_LIMIT = 5;

// Traffic sources are arbitrary UTM/referrer values, so only this known set is
// translated. Anything else renders exactly as it was recorded rather than as a
// missing i18n key path.
const TRANSLATED_INSIGHT_SOURCES = new Set([
  'direct',
  'open_roles',
  'open_roles_more',
  'social',
  'contact',
  'form',
  'form_submit',
  'website',
  'owner_website',
  'portfolio',
  'visit',
  'map',
  'image',
  'team_member',
  // The Links block's tracked clicks report this source; without it the
  // breakdown listed a raw lowercase "links" row, in Arabic too.
  'links',
]);

function insightsSourceLabel(t: (key: never) => string, source: string) {
  return TRANSLATED_INSIGHT_SOURCES.has(source)
    ? t(`publicProfile.openRoles.insights.sources.${source}` as never)
    : source;
}

function SectionTabs({
  active,
  onChange,
}: {
  active: OpenRolesTabId;
  onChange: (tab: OpenRolesTabId) => void;
}) {
  const { t } = useTranslation('common');
  const { theme } = useTheme();

  return (
    <View style={styles.tabsRow}>
      {TAB_ITEMS.map((tab) => {
        const Icon = tab.icon;
        const isActive = active === tab.id;
        return (
          <Pressable
            key={tab.id}
            onPress={() => onChange(tab.id)}
            style={({ pressed }) => [
              styles.tabButton,
              {
                opacity: pressed ? 0.8 : 1,
                backgroundColor: isActive ? theme.text : theme.surface,
                borderColor: theme.borderSubtle,
              },
            ]}
          >
            <Icon size={17} color={isActive ? theme.background : theme.textTertiary} strokeWidth={1.9} />
            <Text
              style={[
                styles.tabLabel,
                {
                  color: isActive ? theme.background : theme.textTertiary,
                },
              ]}
            >
              {t(tab.labelKey as never)}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

const LIFECYCLE_ACTION_LABEL_KEY: Record<JobLifecycleAction, string> = {
  publish: 'jobFlow.lifecycle.publish',
  offline: 'jobFlow.lifecycle.takeOffline',
  reopen: 'jobFlow.lifecycle.reopen',
  archive: 'jobFlow.lifecycle.archive',
  delete: 'jobFlow.lifecycle.delete',
};

/**
 * Company-mode role row (PRD §6.6): every non-archived job with its Draft /
 * Live / Offline label plus Edit and the context-appropriate Publish, Take
 * offline, or Reopen action.
 */
function CompanyRoleRow({
  job,
  onEdit,
  onAction,
  isPending,
}: {
  job: CompanyJobSummary;
  onEdit: () => void;
  onAction: (action: JobLifecycleAction) => void;
  isPending: boolean;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation('common');
  const { t: tEmployer } = useTranslation('employer');
  const lifecycle = getCompanyJobLifecycle(job.status);
  const statusColor =
    lifecycle === 'live'
      ? colors.primary
      : lifecycle === 'draft'
        ? theme.textTertiary
        : colors.danger;
  // Archive stays out of the sheet's compact rows: publish/offline/reopen only.
  const actions = availableJobLifecycleActions(job.status).filter(
    (action) => action !== 'archive',
  );

  return (
    <View
      style={[
        styles.roleCard,
        styles.companyRoleCard,
        { backgroundColor: theme.surface, borderColor: theme.borderSubtle },
      ]}
    >
      <View style={styles.companyRoleHeader}>
        <View style={styles.roleBody}>
          <Text style={[styles.roleTitle, { color: theme.text }]} numberOfLines={1}>
            {job.title}
          </Text>
          <Text style={[styles.roleSubline, { color: theme.textTertiary }]} numberOfLines={2}>
            {getCompanyJobMetaLine(t, job)}
          </Text>
        </View>

        <View style={styles.roleRightWrap}>
          <View style={[styles.statusChip, { backgroundColor: `${statusColor}14` }]}>
            <Text style={[styles.statusChipText, { color: statusColor }]}>
              {formatCompanyJobLifecycle(t, job.status)}
            </Text>
          </View>
          <View style={[styles.applicantBadge, { backgroundColor: '#F0ECFF' }]}>
            <Users size={12} color="#5A2AFF" strokeWidth={2.1} />
            <Text style={styles.applicantBadgeText}>{job.applicationCount}</Text>
          </View>
        </View>
      </View>

      <View style={[styles.companyRoleActions, { borderTopColor: theme.borderSubtle }]}>
        <Pressable
          onPress={onEdit}
          disabled={isPending}
          style={({ pressed }) => [
            styles.roleActionButton,
            { borderColor: theme.borderSubtle, opacity: pressed || isPending ? 0.7 : 1 },
          ]}
        >
          <Pencil size={13} color={theme.textSecondary} strokeWidth={2.1} />
          <Text style={[styles.roleActionText, { color: theme.textSecondary }]}>
            {tEmployer('jobFlow.lifecycle.edit')}
          </Text>
        </Pressable>

        {actions.map((action) => (
          <Pressable
            key={action}
            onPress={() => onAction(action)}
            disabled={isPending}
            style={({ pressed }) => [
              styles.roleActionButton,
              {
                borderColor:
                  action === 'offline' ? `${colors.danger}55` : theme.borderSubtle,
                opacity: pressed || isPending ? 0.7 : 1,
              },
            ]}
          >
            <Text
              style={[
                styles.roleActionText,
                {
                  color:
                    action === 'offline' ? colors.danger : colors.primary,
                },
              ]}
            >
              {tEmployer(LIFECYCLE_ACTION_LABEL_KEY[action] as never) as string}
            </Text>
          </Pressable>
        ))}
      </View>
    </View>
  );
}

function LayoutPreview({ layoutId }: { layoutId: PublicProfileOpenRolesLayout }) {
  const { theme } = useTheme();

  if (layoutId === 'tabs') {
    return (
      <View style={[styles.previewWrap, { backgroundColor: theme.background, borderColor: theme.borderSubtle }]}> 
        <View style={[styles.tabsMiniRow, { borderBottomColor: theme.borderSubtle }]}> 
          <View style={[styles.tabsMiniItemActive, { borderBottomColor: '#5A2AFF' }]} />
          <View style={[styles.tabsMiniItem, { backgroundColor: theme.borderSubtle }]} />
          <View style={[styles.tabsMiniItem, { backgroundColor: theme.borderSubtle }]} />
        </View>
        {[0, 1].map((i) => (
          <View key={i} style={[styles.previewLineRow, { borderBottomColor: i === 0 ? theme.borderSubtle : 'transparent' }]}>
            <View style={[styles.previewLineWide, { backgroundColor: '#C7C7C3' }]} />
            <View style={[styles.previewArrowPill, { backgroundColor: '#EEE8FF' }]} />
          </View>
        ))}
      </View>
    );
  }

  if (layoutId === 'cards') {
    return (
      <View style={styles.previewStack}>
        {[0, 1].map((i) => (
          <View
            key={i}
            style={[styles.previewCardMini, { backgroundColor: theme.background, borderColor: theme.borderSubtle }]}
          >
            <View style={[styles.previewAvatar, { backgroundColor: theme.borderSubtle }]} />
            <View style={styles.previewCardBody}>
              <View style={[styles.previewLineWide, { backgroundColor: '#C7C7C3' }]} />
              <View style={[styles.previewLineShort, { backgroundColor: theme.borderSubtle }]} />
            </View>
            <View style={styles.previewActionButton} />
          </View>
        ))}
      </View>
    );
  }

  return (
    <View style={[styles.previewWrap, { backgroundColor: theme.background, borderColor: theme.borderSubtle }]}> 
      {[0, 1, 2].map((i) => (
        <View key={i} style={[styles.compactLineRow, { borderBottomColor: i < 2 ? theme.borderSubtle : 'transparent' }]}>
          <View style={[styles.previewLineWide, { backgroundColor: '#C7C7C3' }]} />
          <View style={[styles.previewLineTiny, { backgroundColor: theme.borderSubtle }]} />
        </View>
      ))}
    </View>
  );
}

/**
 * Stands in for a metric the account is not entitled to read.
 *
 * The gated payload carries a structural 0 for these fields, and printing that
 * 0 claims a measurement nobody took. An em dash plus the same Lock/BRAND pill
 * the CV analytics upsell uses says "not shown", and the row itself opens the
 * paywall so there is an affordance where the number used to be.
 */
function LockedMetricValue({
  onUpgrade,
  large,
}: {
  onUpgrade: () => void;
  large?: boolean;
}) {
  const { t } = useTranslation('common');
  const { theme } = useTheme();
  return (
    <Pressable
      onPress={onUpgrade}
      accessibilityRole="button"
      accessibilityLabel={t('publicProfile.openRoles.insights.lockedA11y')}
      style={({ pressed }) => [styles.lockedValue, { opacity: pressed ? 0.7 : 1 }]}
    >
      <Text
        style={[
          large ? styles.proMetricValue : styles.metricValue,
          { color: theme.textTertiary },
        ]}
      >
        {t('publicProfile.openRoles.insights.noData')}
      </Text>
      <View style={[styles.lockedPill, { borderColor: theme.borderSubtle }]}>
        <Lock size={11} color={BRAND} strokeWidth={2.2} />
        <Text style={styles.lockedPillText}>
          {t('publicProfile.openRoles.insights.locked')}
        </Text>
      </View>
    </Pressable>
  );
}

export function ProfileOpenRolesSheet() {
  const { t, i18n } = useTranslation('common');
  const { theme } = useTheme();
  const haptics = useHaptics();
  const toast = useToast();
  const router = useRouter();

  const { setBlocks, routeMode } = usePublicProfileBuilderModel();
  const {
    isCompanyMode,
    openRolesBlock,
    jobs,
    stats,
    isLoading,
    error,
    refetch,
  } = useCompanyOpenRoles();
  const lifecycleActions = useJobLifecycleActions();

  const [tab, setTab] = useState<OpenRolesTabId>('jobs');
  const [insightsRange, setInsightsRange] = useState<InsightsRangeId>('7d');
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);
  const thumbnailPickInFlight = useRef(false);
  const rolesLayout = getOpenRolesLayout(openRolesBlock);
  const talentPoolEnabled = isTalentPoolEnabled(openRolesBlock);
  const thumbnailUrl = getOpenRolesThumbnail(openRolesBlock);
  // The builder state loads a hardcoded 30d window, so the range chips need
  // their own fetch. This endpoint gates the detailed breakdown behind the
  // analytics entitlement — the builder route returned it ungated, so the
  // builder-state `analytics` must NOT seed or back-fill this query: on a free
  // plan its real numbers would paint first and then turn into the gated
  // zeros, which reads as "my page stopped getting clicks".
  const insightsQuery = useQuery<PublicProfileAnalyticsSummary>({
    queryKey: ['public-profile-insights', routeMode, insightsRange],
    queryFn: () =>
      fetchPublicProfileAnalytics({ mode: routeMode, range: insightsRange }),
    enabled: tab === 'insights',
    staleTime: 15_000,
    // `insightsRange` is part of the key, so tapping a range chip starts a
    // fresh cache entry and `data` would go undefined mid-flight. That blanks
    // the numbers and — worse — flips `detail` back to unknown, which reads as
    // *unlocked*: a free plan would watch its upsell card unmount and every
    // lock turn into an em dash until the refetch lands. Keeping the previous
    // range's payload holds both the numbers and the gate steady, while a cold
    // start still has no data at all and stays unlocked-until-known.
    placeholderData: keepPreviousData,
  });
  // The server says which payload it sent; we do not re-derive it.
  //
  // Asking `useEntitlement('individual.basic_analytics')` here was wrong in
  // both directions. The key is `planSet: 'individual'`, and a snapshot taken
  // inside a company workspace carries `planSet: 'employer'`, which
  // `evaluateEntitlement` denies as `wrong_tier` before any tier comparison —
  // so the tab was locked for every employer on every plan, and the upgrade
  // CTA it offered could not unlock it (buying Plus raises the seeker
  // snapshot, not the employer one). Meanwhile the API evaluates the seeker
  // snapshot, so a user who WAS entitled got the detailed payload and had
  // dashes painted over it.
  //
  // `detail` is unknown until the payload lands, and that is deliberately not
  // "locked": an entitled user must not see a paywall flash on cold start.
  const insightsDetail = insightsQuery.data?.detail ?? null;
  const canSeeDetailedInsights = insightsDetail !== 'counts_only';
  // `/profile/open-roles` and `/company/open-roles` are iOS form sheets:
  // dismissTo swaps this sheet for the paywall in one native transition
  // instead of stacking a second sheet (dead taps once it closes).
  const openUpgrade = useUpgradeFromFormSheet();
  const openInsightsUpgrade = useCallback(() => {
    haptics.trigger('medium');
    openUpgrade({
      placement: 'analytics',
      entryPoint: 'public_profile.open_roles_insights',
      planSet: 'individual',
      recommendedTier: 'plus',
      featureKey: 'individual.basic_analytics',
      reason: 'wrong_tier',
    });
  }, [haptics, openUpgrade]);
  const insights = insightsQuery.data ?? null;
  const analyticsTotals = insights?.totals ?? ZERO_INSIGHTS_TOTALS;
  const analyticsSeries = useMemo(
    () => insights?.breakdown.series ?? [],
    [insights],
  );
  const analyticsPeakDay = useMemo(() => {
    if (!analyticsSeries.length) {
      return t('publicProfile.openRoles.insights.noData');
    }
    const peak = analyticsSeries.reduce(
      (best, point) => (point.views > best.views ? point : best),
      analyticsSeries[0],
    );
    return formatAnalyticsDate(peak.date, i18n.language);
  }, [analyticsSeries, i18n.language, t]);
  const topInsightSources = useMemo(
    () => getTopSourceShares(insights?.breakdown.sources, INSIGHTS_SOURCE_LIMIT),
    [insights],
  );
  // The only block-scoped figure the summary carries; everything else is
  // page-wide and is labelled as such in the UI.
  const openRolesClicks = getOpenRolesBlockClicks(
    insights?.breakdown.blockClicks,
    openRolesBlock?.id,
  );
  // Gated apply clicks arrive as 0, and `getViewToApplyRate(views, 0)` happily
  // returns 0 — "0.0%" reads as a measured floor rather than a missing figure.
  // Without the entitlement there is no rate to state at all.
  const viewToApplyRate = canSeeDetailedInsights
    ? getViewToApplyRate(analyticsTotals.views, analyticsTotals.jobApplyClicks)
    : null;

  const setRolesLayout = useCallback(
    (layout: PublicProfileOpenRolesLayout) => {
      if (!openRolesBlock) return;
      setBlocks((prev) =>
        prev.map((block) =>
          block.id === openRolesBlock.id
            ? withOpenRolesLayout(block, layout)
            : block,
        ),
      );
    },
    [openRolesBlock, setBlocks],
  );

  const setTalentPoolEnabled = useCallback(
    (enabled: boolean) => {
      if (!openRolesBlock) return;
      setBlocks((prev) =>
        prev.map((block) =>
          block.id === openRolesBlock.id
            ? withTalentPoolEnabled(block, enabled)
            : block,
        ),
      );
    },
    [openRolesBlock, setBlocks],
  );

  const setThumbnail = useCallback(
    (url: string | null) => {
      if (!openRolesBlock) return;
      setBlocks((prev) =>
        prev.map((block) =>
          block.id === openRolesBlock.id
            ? withOpenRolesThumbnail(block, url)
            : block,
        ),
      );
    },
    [openRolesBlock, setBlocks],
  );

  const onPickThumbnail = useCallback(async () => {
    // Ref guard rather than the uploading flag: the flag only flips once a file
    // is actually chosen, so a second tap while the OS picker is open would
    // otherwise stack two pickers.
    if (!openRolesBlock || thumbnailPickInFlight.current) return;
    thumbnailPickInFlight.current = true;
    try {
      // Square crop: the thumbnail renders in a 1:1 tile next to the section
      // heading, so cropping here beats letting the platform letterbox it.
      const picked = await pickImageForUpload({ aspect: [1, 1] });
      if (!picked) return;
      setIsUploadingThumbnail(true);
      const fileUrl = await uploadImageAsset(picked, {
        kind: 'profile-block-thumbnail',
        fileName: picked.name,
        mimeType: picked.type,
      });
      setThumbnail(fileUrl);
      haptics.trigger('selection');
      toast.show({ title: t('publicProfile.openRoles.thumbnailUploaded') });
    } catch (error) {
      toast.show({
        title:
          error instanceof Error
            ? error.message
            : t('publicProfile.openRoles.thumbnailUploadFailed'),
      });
    } finally {
      thumbnailPickInFlight.current = false;
      setIsUploadingThumbnail(false);
    }
  }, [haptics, openRolesBlock, setThumbnail, t, toast]);

  const onRemoveThumbnail = useCallback(() => {
    setThumbnail(null);
    haptics.trigger('selection');
    toast.show({ title: t('publicProfile.openRoles.thumbnailRemoved') });
  }, [haptics, setThumbnail, t, toast]);

  const onDeleteBlock = useCallback(() => {
    if (!openRolesBlock) return;

    setBlocks((prev) => prev.filter((block) => block.id !== openRolesBlock.id));
    haptics.trigger('selection');
    toast.show({ title: t('publicProfile.openRoles.deleted') });
  }, [haptics, openRolesBlock, setBlocks, t, toast]);

  return (
    <ProfileSheetScaffold
      title={t('publicProfile.openRoles.title')}
      subtitle={t('publicProfile.openRoles.subtitle')}
      compact
    >
      {!openRolesBlock ? (
        <ProfileGroupCard>
          <View style={styles.emptyStateWrap}>
            <Text style={[styles.emptyStateTitle, { color: theme.text }]}>
              {t(
                (isCompanyMode
                  ? 'publicProfile.openRoles.noJobs'
                  : 'publicProfile.openRoles.emptyTitle') as never,
              )}
            </Text>
            <Text style={[styles.emptyStateSubtitle, { color: theme.textTertiary }]}>
              {t(
                (isCompanyMode
                  ? 'publicProfile.openRoles.noJobsHint'
                  : 'publicProfile.openRoles.emptySubtitle') as never,
              )}
            </Text>
          </View>
        </ProfileGroupCard>
      ) : (
        <>
          <View style={styles.headingRow}>
            <Text style={[styles.sheetHeading, { color: theme.text }]}>
              {openRolesBlock.customTitle || t('publicProfile.blocks.openroles.title')}
            </Text>
            <Pressable
              onPress={() => {
                if (isCompanyMode) {
                  router.push('/(employer)/jobs' as never);
                }
              }}
              style={({ pressed }) => [
                styles.headingIconButton,
                {
                  opacity: pressed ? 0.75 : 1,
                  borderColor: theme.borderSubtle,
                  backgroundColor: theme.surface,
                },
              ]}
            >
              <Pencil size={16} color={theme.textTertiary} strokeWidth={2.1} />
            </Pressable>
          </View>

          <SectionTabs active={tab} onChange={setTab} />

          {tab === 'jobs' ? (
            <View style={styles.sectionBody}>
              {isCompanyMode && isLoading ? (
                <ProfileGroupCard>
                  <View style={styles.emptyStateWrap}>
                    <Text style={[styles.emptyStateTitle, { color: theme.text }]}>
                      {t('jobPost.entry.loading')}
                    </Text>
                  </View>
                </ProfileGroupCard>
              ) : isCompanyMode && error ? (
                <ProfileGroupCard>
                  <View style={styles.emptyStateWrap}>
                    <Text style={[styles.emptyStateTitle, { color: theme.text }]}>
                      {t('jobPost.entry.loadFailed')}
                    </Text>
                    <Text style={[styles.emptyStateSubtitle, { color: theme.textTertiary }]}>
                      {t('jobPost.entry.loadFailedHint')}
                    </Text>
                    <Pressable
                      onPress={() => void refetch()}
                      style={({ pressed }) => [
                        styles.retryButton,
                        {
                          opacity: pressed ? 0.8 : 1,
                          borderColor: theme.borderSubtle,
                          backgroundColor: theme.surface,
                        },
                      ]}
                    >
                      <Text style={[styles.retryButtonText, { color: theme.text }]}>
                        {t('actions.retry')}
                      </Text>
                    </Pressable>
                  </View>
                </ProfileGroupCard>
              ) : isCompanyMode && jobs.length > 0 ? (
                <View style={styles.rolesList}>
                  {jobs.map((job) => (
                    <CompanyRoleRow
                      key={job.id}
                      job={job}
                      onEdit={() =>
                        router.push(
                          `/(employer)/company/create-job?jobId=${job.id}` as never,
                        )
                      }
                      onAction={(action) => void lifecycleActions.run(action, job.id)}
                      isPending={lifecycleActions.isPending(job.id)}
                    />
                  ))}
                </View>
              ) : (
                <ProfileGroupCard>
                  <View style={styles.emptyStateWrap}>
                    <Text style={[styles.emptyStateTitle, { color: theme.text }]}>
                      {t('publicProfile.openRoles.noJobs')}
                    </Text>
                    <Text style={[styles.emptyStateSubtitle, { color: theme.textTertiary }]}>
                      {t('publicProfile.openRoles.noJobsHint')}
                    </Text>
                  </View>
                </ProfileGroupCard>
              )}

              <ProfileGroupCard>
                <View style={styles.talentPoolRow}>
                  <AppSwitch
                    value={talentPoolEnabled}
                    onValueChange={setTalentPoolEnabled}
                    accessibilityLabel={t('publicProfile.openRoles.talentLibrary')}
                  />
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.talentPoolTitle, { color: theme.text }]}>
                      {t('publicProfile.openRoles.talentLibrary')}
                    </Text>
                    <Text style={[styles.talentPoolDesc, { color: theme.textTertiary }]}>
                      {t('publicProfile.openRoles.talentLibraryDesc')}
                    </Text>
                  </View>
                  <View style={styles.applicantBadge}>
                    <Users size={12} color="#5A2AFF" strokeWidth={2.1} />
                    <Text style={styles.applicantBadgeText}>
                      {isCompanyMode ? stats.totalApplications : 0}
                    </Text>
                  </View>
                </View>
              </ProfileGroupCard>

              <Pressable
                onPress={() =>
                  isCompanyMode
                    ? router.push('/(employer)/company/create-job')
                    : undefined
                }
                style={({ pressed }) => [styles.primaryAction, { opacity: pressed ? 0.86 : 1 }]}
              >
                <Plus size={17} color="#FFFFFF" strokeWidth={2.3} />
                <Text style={styles.primaryActionText}>{t('publicProfile.openRoles.postJob')}</Text>
              </Pressable>

              <View style={[styles.feedbackRow, { borderTopColor: theme.borderSubtle }]}> 
                <Text style={[styles.feedbackText, { color: theme.textTertiary }]}>
                  {t('publicProfile.openRoles.gotIdeas')}{' '}
                </Text>
                <Pressable onPress={() => {}}>
                  <Text style={styles.feedbackLink}>{t('publicProfile.openRoles.shareFeedback')}</Text>
                </Pressable>
              </View>

              {!isCompanyMode ? (
                <Pressable
                  onPress={onDeleteBlock}
                  style={({ pressed }) => [styles.deleteButton, { opacity: pressed ? 0.78 : 1 }]}
                >
                  <Text style={styles.deleteButtonText}>{t('publicProfile.openRoles.deleteBlock')}</Text>
                </Pressable>
              ) : null}
            </View>
          ) : null}

          {tab === 'layout' ? (
            <View style={styles.sectionBody}>
              <ProfileSectionHeader title={t('publicProfile.openRoles.layoutHeading')} />
              <View
                style={styles.layoutOptionsWrap}
                accessibilityRole="radiogroup"
              >
                {LAYOUT_OPTIONS.map((option) => {
                  const active = rolesLayout === option.id;
                  return (
                    <Pressable
                      key={option.id}
                      onPress={() => setRolesLayout(option.id)}
                      accessibilityRole="radio"
                      // `checked`, not `selected`: that is the only state
                      // Android/iOS speak for a radio, and react-native-web
                      // reads neither — it needs the aria prop directly.
                      accessibilityState={{ checked: active }}
                      aria-checked={active}
                      style={({ pressed }) => [
                        styles.layoutOption,
                        {
                          opacity: pressed ? 0.88 : 1,
                          borderColor: active ? '#5A2AFF' : theme.borderSubtle,
                          backgroundColor: active ? '#F3F0FF' : theme.surface,
                        },
                      ]}
                    >
                      <View style={styles.layoutHeaderRow}>
                        <View
                          style={[
                            styles.radio,
                            {
                              borderColor: active ? '#5A2AFF' : theme.borderSubtle,
                              backgroundColor: active ? '#5A2AFF' : 'transparent',
                            },
                          ]}
                        >
                          {active ? <View style={styles.radioDot} /> : null}
                        </View>
                        <Text style={[styles.layoutTitle, { color: theme.text }]}>{t(option.titleKey as never)}</Text>
                        <Text style={[styles.layoutDesc, { color: theme.textTertiary }]}>
                          {t(option.descKey as never)}
                        </Text>
                      </View>
                      <LayoutPreview layoutId={option.id} />
                    </Pressable>
                  );
                })}
              </View>
            </View>
          ) : null}

          {tab === 'thumbnail' ? (
            <View style={styles.sectionBody}>
              <ProfileGroupCard>
                <Pressable
                  onPress={onPickThumbnail}
                  disabled={isUploadingThumbnail}
                  accessibilityRole="button"
                  accessibilityLabel={t(
                    (thumbnailUrl
                      ? 'publicProfile.openRoles.thumbnailReplace'
                      : 'publicProfile.openRoles.thumbnailTitle') as never,
                  )}
                  style={({ pressed }) => [styles.thumbnailRow, { opacity: pressed ? 0.8 : 1 }]}
                >
                  {thumbnailUrl ? (
                    <View style={[styles.thumbnailImageWrap, { borderColor: theme.borderSubtle }]}>
                      <Image
                        source={{ uri: thumbnailUrl }}
                        style={styles.thumbnailImage}
                        resizeMode="cover"
                      />
                      {isUploadingThumbnail ? (
                        <View style={styles.thumbnailBusyOverlay}>
                          <ActivityIndicator size="small" color="#FFFFFF" />
                        </View>
                      ) : null}
                    </View>
                  ) : (
                    <View style={[styles.thumbnailPlaceholder, { borderColor: theme.borderSubtle }]}>
                      {isUploadingThumbnail ? (
                        <ActivityIndicator size="small" color={theme.textTertiary} />
                      ) : (
                        <Plus size={24} color={theme.textTertiary} strokeWidth={1.9} />
                      )}
                    </View>
                  )}
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.thumbnailTitle, { color: theme.text }]}>
                      {t(
                        (thumbnailUrl
                          ? 'publicProfile.openRoles.thumbnailSetTitle'
                          : 'publicProfile.openRoles.thumbnailTitle') as never,
                      )}
                    </Text>
                    <Text style={[styles.thumbnailDesc, { color: theme.textTertiary }]}>
                      {isUploadingThumbnail
                        ? t('publicProfile.openRoles.thumbnailUploading')
                        : t(
                            (thumbnailUrl
                              ? 'publicProfile.openRoles.thumbnailSetDesc'
                              : 'publicProfile.openRoles.thumbnailDesc') as never,
                          )}
                    </Text>
                  </View>
                </Pressable>

                {thumbnailUrl ? (
                  <View style={[styles.thumbnailActions, { borderTopColor: theme.borderSubtle }]}>
                    <Pressable
                      onPress={onPickThumbnail}
                      disabled={isUploadingThumbnail}
                      accessibilityRole="button"
                      style={({ pressed }) => [
                        styles.thumbnailActionButton,
                        {
                          opacity: pressed || isUploadingThumbnail ? 0.65 : 1,
                          borderColor: theme.borderSubtle,
                        },
                      ]}
                    >
                      <Text style={[styles.thumbnailActionText, { color: theme.text }]}>
                        {t('publicProfile.openRoles.thumbnailReplace')}
                      </Text>
                    </Pressable>
                    <Pressable
                      onPress={onRemoveThumbnail}
                      disabled={isUploadingThumbnail}
                      accessibilityRole="button"
                      style={({ pressed }) => [
                        styles.thumbnailActionButton,
                        {
                          opacity: pressed || isUploadingThumbnail ? 0.65 : 1,
                          borderColor: theme.borderSubtle,
                        },
                      ]}
                    >
                      <Text style={[styles.thumbnailActionText, { color: colors.danger }]}>
                        {t('publicProfile.openRoles.thumbnailRemove')}
                      </Text>
                    </Pressable>
                  </View>
                ) : null}
              </ProfileGroupCard>
            </View>
          ) : null}

          {tab === 'insights' ? (
            <View style={styles.sectionBody}>
              <View style={styles.insightsRangeRow}>
                {(['7d', '30d', 'all'] as const).map((range) => {
                  const active = insightsRange === range;
                  return (
                    <Pressable
                      key={range}
                      onPress={() => setInsightsRange(range)}
                      accessibilityRole="button"
                      accessibilityState={{ selected: active }}
                      style={({ pressed }) => [
                        styles.rangeButton,
                        {
                          opacity: pressed ? 0.82 : 1,
                          borderColor: active ? '#5A2AFF' : theme.borderSubtle,
                          backgroundColor: active ? '#F3F0FF' : 'transparent',
                        },
                      ]}
                    >
                      <Text style={[styles.rangeText, { color: active ? '#5A2AFF' : theme.textTertiary }]}>
                        {range === 'all' ? t('publicProfile.openRoles.insights.all') : range}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>

              {insightsQuery.isFetching ? (
                <View style={styles.insightsStatusRow}>
                  <ActivityIndicator size="small" color={theme.textTertiary} />
                  <Text style={[styles.insightsStatusText, { color: theme.textTertiary }]}>
                    {t('publicProfile.openRoles.insights.updating')}
                  </Text>
                </View>
              ) : null}

              {insightsQuery.isError && !insights ? (
                <View style={styles.insightsStatusRow}>
                  <Text style={[styles.insightsStatusText, { color: colors.danger }]}>
                    {t('publicProfile.openRoles.insights.error')}
                  </Text>
                </View>
              ) : null}

              {/* Named before the numbers, so the dashes below are read as
                  "not shown on this plan" rather than "nothing happened". */}
              {!canSeeDetailedInsights ? (
                <Pressable
                  onPress={openInsightsUpgrade}
                  accessibilityRole="button"
                  style={({ pressed }) => [
                    styles.insightsUpsell,
                    {
                      borderColor: theme.borderSubtle,
                      backgroundColor: theme.surfaceSecondary,
                      opacity: pressed ? 0.85 : 1,
                    },
                  ]}
                >
                  <View style={styles.insightsUpsellHeader}>
                    {/* The same pill the locked rows use. `AnalyticsLockBadge`
                        is a hardcoded "PRO", which contradicted this card's
                        own title: `individual.basic_analytics` unlocks at
                        Plus, and the CTA below recommends Plus. */}
                    <View
                      style={[
                        styles.lockedPill,
                        { borderColor: theme.borderSubtle },
                      ]}
                    >
                      <Lock size={11} color={BRAND} strokeWidth={2.2} />
                      <Text style={styles.lockedPillText}>
                        {t('publicProfile.openRoles.insights.locked')}
                      </Text>
                    </View>
                  </View>
                  <Text style={[styles.insightsUpsellTitle, { color: theme.text }]}>
                    {t('publicProfile.openRoles.insights.lockedTitle')}
                  </Text>
                  <Text
                    style={[styles.insightsUpsellBody, { color: theme.textTertiary }]}
                  >
                    {t('publicProfile.openRoles.insights.lockedSubtitle')}
                  </Text>
                  <Text style={styles.insightsUpsellCta}>
                    {t('publicProfile.openRoles.insights.lockedCta')}
                  </Text>
                </Pressable>
              ) : null}

              <ProfileGroupCard>
                <View style={styles.metricRow}>
                  <Text style={[styles.metricLabel, { color: theme.textTertiary }]}>
                    {t('publicProfile.openRoles.insights.totalViews')}
                  </Text>
                  {/* Total views is the one figure the free payload measures.
                      Until it lands there is nothing to state, so no zero. */}
                  <Text style={[styles.metricValue, { color: theme.text }]}>
                    {insights
                      ? analyticsTotals.views.toLocaleString()
                      : t('publicProfile.openRoles.insights.noData')}
                  </Text>
                  <Text style={[styles.metricHint, { color: theme.textTertiary }]}>
                    {t('publicProfile.openRoles.insights.scopePageWide')}
                  </Text>
                </View>
                <View style={[styles.metricDivider, { backgroundColor: theme.borderSubtle }]} />
                <View style={styles.metricRow}>
                  <Text style={[styles.metricLabel, { color: theme.textTertiary }]}>
                    {t('publicProfile.openRoles.insights.uniqueVisitors')}
                  </Text>
                  {canSeeDetailedInsights ? (
                    <Text style={[styles.metricValue, { color: theme.text }]}>
                      {insights
                        ? analyticsTotals.uniqueVisitors.toLocaleString()
                        : t('publicProfile.openRoles.insights.noData')}
                    </Text>
                  ) : (
                    <LockedMetricValue onUpgrade={openInsightsUpgrade} />
                  )}
                  <Text style={[styles.metricHint, { color: theme.textTertiary }]}>
                    {t('publicProfile.openRoles.insights.scopePageWide')}
                  </Text>
                </View>
                <View style={[styles.metricDivider, { backgroundColor: theme.borderSubtle }]} />
                <View style={styles.metricRow}>
                  <Text style={[styles.metricLabel, { color: theme.textTertiary }]}>
                    {t('publicProfile.openRoles.insights.applyClicks')}
                  </Text>
                  {canSeeDetailedInsights ? (
                    <Text style={[styles.metricValue, { color: theme.text }]}>
                      {insights
                        ? analyticsTotals.jobApplyClicks.toLocaleString()
                        : t('publicProfile.openRoles.insights.noData')}
                    </Text>
                  ) : (
                    <LockedMetricValue onUpgrade={openInsightsUpgrade} />
                  )}
                </View>
                <View style={[styles.metricDivider, { backgroundColor: theme.borderSubtle }]} />
                <View style={styles.metricRow}>
                  <Text style={[styles.metricLabel, { color: theme.textTertiary }]}>
                    {t('publicProfile.openRoles.insights.peakDay')}
                  </Text>
                  {canSeeDetailedInsights ? (
                    <Text style={[styles.metricValue, { color: theme.text }]}>{analyticsPeakDay}</Text>
                  ) : (
                    <LockedMetricValue onUpgrade={openInsightsUpgrade} />
                  )}
                </View>
              </ProfileGroupCard>

              <ProfileGroupCard>
                <View style={styles.proMetricRow}>
                  <View style={styles.proMetricLabelWrap}>
                    <Text style={[styles.metricLabel, { color: theme.textTertiary }]}>
                      {t('publicProfile.openRoles.insights.openRolesClicks')}
                    </Text>
                    <Text style={[styles.metricHint, { color: theme.textTertiary }]}>
                      {t('publicProfile.openRoles.insights.scopeBlock')}
                    </Text>
                  </View>
                  {canSeeDetailedInsights ? (
                    <Text style={[styles.proMetricValue, { color: theme.text }]}>
                      {insights
                        ? openRolesClicks.toLocaleString()
                        : t('publicProfile.openRoles.insights.noData')}
                    </Text>
                  ) : (
                    <LockedMetricValue onUpgrade={openInsightsUpgrade} large />
                  )}
                </View>
                <View style={[styles.metricDivider, { backgroundColor: theme.borderSubtle }]} />
                <View style={styles.proMetricRow}>
                  <View style={styles.proMetricLabelWrap}>
                    <Text style={[styles.metricLabel, { color: theme.textTertiary }]}>
                      {t('publicProfile.openRoles.insights.viewToApply')}
                    </Text>
                    <Text style={[styles.metricHint, { color: theme.textTertiary }]}>
                      {t('publicProfile.openRoles.insights.viewToApplyHint')}
                    </Text>
                  </View>
                  {canSeeDetailedInsights ? (
                    <Text style={[styles.proMetricValue, { color: theme.text }]}>
                      {viewToApplyRate === null
                        ? t('publicProfile.openRoles.insights.noData')
                        : `${viewToApplyRate.toFixed(1)}%`}
                    </Text>
                  ) : (
                    <LockedMetricValue onUpgrade={openInsightsUpgrade} large />
                  )}
                </View>
              </ProfileGroupCard>

              <ProfileGroupCard>
                <View style={styles.sourceHeader}>
                  <Text style={[styles.metricLabel, { color: theme.textTertiary }]}>
                    {t('publicProfile.openRoles.insights.sourceBreakdown')}
                  </Text>
                  <Text style={[styles.metricHint, { color: theme.textTertiary }]}>
                    {t('publicProfile.openRoles.insights.scopePageWide')}
                  </Text>
                </View>
                {/* An empty source list on a gated payload is not "no traffic";
                    the server never ran the query. Say so instead. */}
                {!canSeeDetailedInsights ? (
                  <Pressable
                    onPress={openInsightsUpgrade}
                    accessibilityRole="button"
                    style={({ pressed }) => [
                      styles.sourceLockedRow,
                      { opacity: pressed ? 0.7 : 1 },
                    ]}
                  >
                    <Lock size={13} color={BRAND} strokeWidth={2.2} />
                    <Text style={[styles.sourceLockedText, { color: theme.textTertiary }]}>
                      {t('publicProfile.openRoles.insights.sourceLocked')}
                    </Text>
                  </Pressable>
                ) : topInsightSources.length === 0 ? (
                  <Text style={[styles.sourceEmptyText, { color: theme.textTertiary }]}>
                    {t('publicProfile.openRoles.insights.sourceEmpty')}
                  </Text>
                ) : (
                  <View style={styles.sourceList}>
                    {topInsightSources.map((entry) => (
                      <View key={entry.source} style={styles.sourceRow}>
                        <View style={styles.sourceRowHeader}>
                          <Text
                            style={[styles.sourceLabel, { color: theme.text }]}
                            numberOfLines={1}
                          >
                            {insightsSourceLabel(t, entry.source)}
                          </Text>
                          <Text style={[styles.sourceCount, { color: theme.textTertiary }]}>
                            {`${entry.count.toLocaleString()} · ${Math.round(entry.share)}%`}
                          </Text>
                        </View>
                        <View style={[styles.sourceTrack, { backgroundColor: theme.borderSubtle }]}>
                          {/* Floor the width so a non-zero source is never an invisible sliver. */}
                          <View
                            style={[styles.sourceFill, { width: `${Math.max(entry.share, 3)}%` }]}
                          />
                        </View>
                      </View>
                    ))}
                  </View>
                )}
              </ProfileGroupCard>
            </View>
          ) : null}
        </>
      )}
    </ProfileSheetScaffold>
  );
}

const styles = StyleSheet.create({
  headingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing[2],
  },
  sheetHeading: {
    fontSize: 19,
    lineHeight: 24,
    fontWeight: '700',
  },
  headingIconButton: {
    width: 32,
    height: 32,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabsRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: spacing[2],
  },
  tabButton: {
    flex: 1,
    minHeight: 72,
    borderRadius: 14,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  tabLabel: {
    fontSize: 12,
    lineHeight: 14,
    fontWeight: '600',
  },
  sectionBody: {
    gap: spacing[2],
  },
  rolesList: {
    gap: 8,
  },
  roleCard: {
    minHeight: 74,
    borderRadius: 16,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 14,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  roleBody: {
    flex: 1,
    gap: 3,
  },
  companyRoleCard: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: spacing[2],
  },
  companyRoleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  statusChip: {
    borderRadius: 999,
    paddingHorizontal: spacing[2],
    paddingVertical: 5,
  },
  statusChipText: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: '700',
  },
  companyRoleActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[2],
    paddingTop: spacing[2],
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  roleActionButton: {
    minHeight: 34,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: spacing[3],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
  },
  roleActionText: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '600',
  },
  liveBadge: {
    minWidth: 56,
    height: 32,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing[2],
  },
  liveBadgeText: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: '700',
  },
  roleTitle: {
    fontSize: 16,
    lineHeight: 20,
    fontWeight: '600',
  },
  roleSubline: {
    fontSize: 13,
    lineHeight: 18,
  },
  roleRightWrap: {
    alignItems: 'center',
    gap: 8,
  },
  applicantBadge: {
    minWidth: 46,
    height: 34,
    borderRadius: 10,
    paddingHorizontal: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
  },
  applicantBadgeText: {
    fontSize: 14,
    lineHeight: 18,
    fontWeight: '700',
    color: '#5A2AFF',
  },
  closeIconButton: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  talentPoolRow: {
    minHeight: 78,
    paddingHorizontal: 14,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  talentPoolTitle: {
    fontSize: 16,
    lineHeight: 20,
    fontWeight: '600',
    marginBottom: 2,
  },
  talentPoolDesc: {
    fontSize: 13,
    lineHeight: 18,
  },
  primaryAction: {
    minHeight: 52,
    borderRadius: 14,
    backgroundColor: '#5A2AFF',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  primaryActionText: {
    color: '#FFFFFF',
    fontSize: 18,
    lineHeight: 22,
    fontWeight: '700',
  },
  feedbackRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: spacing[3],
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  feedbackText: {
    fontSize: 14,
    lineHeight: 18,
    fontWeight: '500',
  },
  feedbackLink: {
    fontSize: 14,
    lineHeight: 18,
    fontWeight: '600',
    color: '#5A2AFF',
  },
  deleteButton: {
    alignSelf: 'flex-start',
    paddingVertical: 6,
  },
  deleteButtonText: {
    fontSize: 18,
    lineHeight: 22,
    fontWeight: '500',
    color: '#FF3B30',
  },
  layoutOptionsWrap: {
    gap: 12,
  },
  layoutOption: {
    borderRadius: 18,
    borderWidth: 2,
    padding: 14,
    gap: 10,
  },
  layoutHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  radio: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: '#FFFFFF',
  },
  layoutTitle: {
    fontSize: 16,
    lineHeight: 20,
    fontWeight: '700',
  },
  layoutDesc: {
    fontSize: 14,
    lineHeight: 18,
  },
  previewWrap: {
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  },
  tabsMiniRow: {
    minHeight: 34,
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 10,
  },
  tabsMiniItemActive: {
    width: 78,
    height: 2,
    borderBottomWidth: 2,
  },
  tabsMiniItem: {
    width: 50,
    height: 6,
    borderRadius: 3,
    marginBottom: 8,
  },
  previewLineRow: {
    minHeight: 38,
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  previewLineWide: {
    flex: 1,
    height: 8,
    borderRadius: 4,
  },
  previewArrowPill: {
    width: 26,
    height: 20,
    borderRadius: 7,
  },
  previewStack: {
    gap: 8,
  },
  previewCardMini: {
    minHeight: 56,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  previewAvatar: {
    width: 30,
    height: 30,
    borderRadius: 8,
  },
  previewCardBody: {
    flex: 1,
    gap: 5,
  },
  previewLineShort: {
    width: 80,
    height: 6,
    borderRadius: 3,
  },
  previewActionButton: {
    width: 56,
    height: 22,
    borderRadius: 8,
    backgroundColor: '#5A2AFF',
  },
  compactLineRow: {
    minHeight: 30,
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  previewLineTiny: {
    width: 36,
    height: 6,
    borderRadius: 3,
  },
  thumbnailRow: {
    minHeight: 106,
    paddingHorizontal: 14,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  thumbnailPlaceholder: {
    width: 86,
    height: 86,
    borderRadius: 18,
    borderWidth: 2,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbnailImageWrap: {
    width: 86,
    height: 86,
    borderRadius: 18,
    borderWidth: 1,
    overflow: 'hidden',
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
  },
  thumbnailBusyOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.35)',
  },
  thumbnailActions: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 14,
    paddingBottom: 14,
    paddingTop: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  thumbnailActionButton: {
    flex: 1,
    minHeight: 42,
    borderRadius: 12,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbnailActionText: {
    fontSize: 15,
    lineHeight: 19,
    fontWeight: '600',
  },
  thumbnailTitle: {
    fontSize: 18,
    lineHeight: 23,
    fontWeight: '700',
    marginBottom: 4,
  },
  thumbnailDesc: {
    fontSize: 15,
    lineHeight: 19,
  },
  insightsRangeRow: {
    flexDirection: 'row',
    gap: 8,
  },
  rangeButton: {
    flex: 1,
    minHeight: 46,
    borderRadius: 14,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rangeText: {
    fontSize: 16,
    lineHeight: 20,
    fontWeight: '700',
  },
  metricRow: {
    minHeight: 80,
    paddingHorizontal: 14,
    paddingVertical: 12,
    justifyContent: 'center',
  },
  metricLabel: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 4,
  },
  metricValue: {
    fontSize: 46,
    lineHeight: 50,
    fontWeight: '700',
  },
  metricDivider: {
    height: StyleSheet.hairlineWidth,
    marginHorizontal: 14,
  },
  metricHint: {
    fontSize: 13,
    lineHeight: 17,
    fontWeight: '500',
    marginTop: 4,
  },
  insightsStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 4,
  },
  insightsStatusText: {
    fontSize: 14,
    lineHeight: 18,
    fontWeight: '500',
  },
  proMetricRow: {
    minHeight: 70,
    paddingHorizontal: 14,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  proMetricLabelWrap: {
    flex: 1,
  },
  proMetricValue: {
    fontSize: 28,
    lineHeight: 32,
    fontWeight: '700',
  },
  sourceHeader: {
    paddingHorizontal: 14,
    paddingTop: 14,
    paddingBottom: 6,
  },
  sourceEmptyText: {
    fontSize: 15,
    lineHeight: 20,
    fontWeight: '500',
    paddingHorizontal: 14,
    paddingBottom: 14,
  },
  sourceLockedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingBottom: 14,
  },
  sourceLockedText: {
    flex: 1,
    fontSize: 15,
    lineHeight: 20,
    fontWeight: '500',
  },
  lockedValue: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  lockedPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  lockedPillText: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: '700',
    letterSpacing: 0.4,
    color: BRAND,
  },
  insightsUpsell: {
    borderWidth: 1,
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingVertical: 14,
    gap: 6,
  },
  insightsUpsellHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  insightsUpsellTitle: {
    fontSize: 16,
    lineHeight: 21,
    fontWeight: '700',
  },
  insightsUpsellBody: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '500',
  },
  insightsUpsellCta: {
    fontSize: 14,
    lineHeight: 19,
    fontWeight: '700',
    marginTop: 2,
    color: BRAND,
  },
  sourceList: {
    paddingBottom: 8,
  },
  sourceRow: {
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  sourceRowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 6,
  },
  sourceLabel: {
    flex: 1,
    fontSize: 15,
    lineHeight: 19,
    fontWeight: '600',
  },
  sourceCount: {
    fontSize: 14,
    lineHeight: 18,
    fontWeight: '600',
  },
  sourceTrack: {
    height: 6,
    borderRadius: 3,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  sourceFill: {
    height: '100%',
    borderRadius: 3,
    backgroundColor: '#5A2AFF',
  },
  upgradeChip: {
    minHeight: 36,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: '#EEE8FF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  upgradeChipText: {
    fontSize: 18,
    lineHeight: 22,
    fontWeight: '700',
    color: '#5A2AFF',
  },
  emptyStateWrap: {
    minHeight: 110,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    gap: 4,
  },
  emptyStateTitle: {
    fontSize: 16,
    lineHeight: 20,
    fontWeight: '700',
    textAlign: 'center',
  },
  emptyStateSubtitle: {
    fontSize: 14,
    lineHeight: 18,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: spacing[2],
    minHeight: 40,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: spacing[3],
    alignItems: 'center',
    justifyContent: 'center',
  },
  retryButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
